import { r as createLogger } from "../logger-BU8YqMGU.js";
import { extractOdomPosition, extractOdomRotation } from "../types/ar-types.js";
import { n as resetDeviceOrientationCache, r as updateDeviceOrientation, t as getLastDeviceOrientation } from "../device-orientation-cache-Cmn72tA0.js";
import { getLatestAbsoluteOrientation } from "../sensors/absolute-orientation.js";
import { eulerToQuaternion, getOdometryPositions, getZeroReference, recordGpsEvent, setZeroPos } from "gps-plus-slam-js";
//#region ../src/state/gps-event-coordinator.ts
const log = createLogger("RecordingCoordinator");
/** Counter for generating unique GPS point IDs */
let gpsEventCounter = 0;
/**
* Clear cached state (for testing or session reset)
*/
function resetCoordinatorState() {
	resetDeviceOrientationCache();
	gpsEventCounter = 0;
}
/**
* Build a RawGpsPoint from GPS position data.
* Returns only raw sensor fields — no derived fields (coordinates, weight,
* zeroRef). Derived fields are computed by the library reducer when the
* action is dispatched (raw-storage pattern).
*
* @param gpsPosition - GPS position from Geolocation API
* @param deviceOrientation - Accepted for signature stability; no longer
*   read (the legacy `compassAbsolute` field it fed is no longer recorded)
* @returns RawGpsPoint ready for the action payload
*/
function buildRawGpsPoint(gpsPosition, deviceOrientation) {
	return {
		id: `gps-${++gpsEventCounter}`,
		latitude: gpsPosition.lat,
		longitude: gpsPosition.lon,
		altitude: gpsPosition.altitude ?? void 0,
		latLongAccuracy: gpsPosition.accuracy,
		altitudeAccuracy: gpsPosition.altitudeAccuracy ?? void 0,
		heading: gpsPosition.heading ?? void 0,
		speed: gpsPosition.speed ?? void 0,
		timestamp: gpsPosition.timestamp
	};
}
/**
* Map a capture-module {@link AbsoluteOrientationReading} to the library's
* {@link RawAbsoluteOrientation} payload shape (the only difference is the
* timestamp field name: capture `timestamp` → payload `sampleTimestamp`).
* Returns `undefined` when the sensor produced no reading (most non-Chrome).
*/
function toRawAbsoluteOrientation(reading) {
	if (!reading) return void 0;
	return {
		quaternion: reading.quaternion,
		referenceFrame: reading.referenceFrame,
		screenAngleDeg: reading.screenAngleDeg,
		sampleTimestamp: reading.timestamp
	};
}
/**
* Build a RecordGpsEventPayload from GPS position and AR pose.
* This is a pure function for testability.
*
* Stores only raw sensor data in the payload. The library reducer
* computes derived fields (coordinates, weight) when the action is
* processed (raw-storage pattern).
*
* @param gpsPosition - GPS position from Geolocation API
* @param arPose - AR pose from WebXR
* @param deviceOrientation - Optional device orientation from sensors
* @param absoluteOrientation - Optional AbsoluteOrientationSensor reading
* @returns RecordGpsEventPayload ready for dispatch
*/
function buildRecordGpsEventPayload(gpsPosition, arPose, deviceOrientation, absoluteOrientation = null) {
	return {
		odomPosition: extractOdomPosition(arPose),
		odomRotation: extractOdomRotation(arPose),
		rawGpsPoint: buildRawGpsPoint(gpsPosition, deviceOrientation),
		rawAbsoluteOrientation: toRawAbsoluteOrientation(absoluteOrientation)
	};
}
/**
* Create a GPS position handler that dispatches combined GPS+AR events.
*
* This is the CRITICAL function that implements the correct architecture:
* - Called when GPS arrives
* - Reads current AR pose
* - Dispatches single combined action
*
* @param config - Configuration with store and getArPose function
* @returns Callback function for GPS watch
*/
function createGpsPositionHandler(config) {
	const { store, getArPose } = config;
	return (position) => {
		if (!store.getState().recording.isRecording) return;
		const arPose = getArPose();
		if (!arPose) {
			log.warn("GPS arrived but no AR pose available");
			return;
		}
		if (!store.getState().gpsData) {
			const zeroRef = {
				lat: position.lat,
				lon: position.lon
			};
			store.dispatch(setZeroPos(zeroRef));
			log.info(`Set zero reference: ${zeroRef.lat}, ${zeroRef.lon}`);
		}
		if (!getZeroReference(store.getState())) {
			log.error("Failed to get zero reference");
			return;
		}
		const payload = buildRecordGpsEventPayload(position, arPose, getLastDeviceOrientation(), getLatestAbsoluteOrientation());
		store.dispatch(recordGpsEvent(payload));
		const eventCount = getOdometryPositions(store.getState()).length;
		log.info(`Recorded GPS event #${eventCount}`);
	};
}
//#endregion
export { buildRawGpsPoint, buildRecordGpsEventPayload, createGpsPositionHandler, eulerToQuaternion, extractOdomPosition, extractOdomRotation, getLastDeviceOrientation, resetCoordinatorState, toRawAbsoluteOrientation, updateDeviceOrientation };
